import {
  hasLevel,
  ladderAccess,
  type MergeCandidate,
  useMergeCandidates,
  useMergeHistory,
  useMergePeople,
  useMergePreview,
  useUndoMerge,
} from '@church/data'
import {
  faArrowRotateLeft,
  faBan,
  faChevronRight,
  faCodeMerge,
  faRightLeft,
  faXmark,
} from '@church/ui-icons'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Icon,
  IconButton,
  ListTitle,
  PageHeader,
  Spinner,
} from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'

/**
 * Duplicate review: candidates on the left, a side-by-side field picker for
 * the pair under review, and the merge history with undo.
 *
 * The screen never decides anything itself — the server's `preview` supplies
 * both the field diff and the `suggested` side, so what the radios show is
 * what a merge would do if you pressed the button without touching them.
 */
export const Route = createFileRoute('/_authed/_shell/people/duplicates')({
  beforeLoad: ({ context }) => {
    // Merging is people:admin; the server enforces, this spares a wall of 403s.
    if (!hasLevel(ladderAccess(context.me), 'people', 'administrator')) {
      throw redirect({ to: '/people' })
    }
  },
  component: DuplicatesScreen,
})

interface Pair {
  winnerId: string
  loserId: string
}

function DuplicatesScreen() {
  const { data: candidates, isPending } = useMergeCandidates()
  const [pair, setPair] = useState<Pair | null>(null)
  useBreadcrumbs([{ label: 'Duplicates' }])

  return (
    <div className="space-y-4">
      <PageHeader title="Duplicates" />

      {pair && <MergePicker pair={pair} onClose={() => setPair(null)} />}

      <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Card bleed>
          <ListTitle title="Likely duplicates" />
          <div className="p-4">
            {isPending ? (
              <Spinner />
            ) : !candidates || candidates.length === 0 ? (
              <EmptyState
                title="No duplicates found"
                hint="Pairs appear here when two records share an email, a phone number, or a close name."
              />
            ) : (
              <ul className="divide-y divide-border-subtle">
                {candidates.map((candidate) => (
                  <CandidateRow
                    key={`${candidate.leftId}:${candidate.rightId}`}
                    candidate={candidate}
                    onReview={(winnerId, loserId) => setPair({ winnerId, loserId })}
                  />
                ))}
              </ul>
            )}
          </div>
        </Card>

        <HistoryCard />
      </div>
    </div>
  )
}

function CandidateRow({
  candidate,
  onReview,
}: {
  candidate: MergeCandidate
  onReview: (winnerId: string, loserId: string) => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onReview(candidate.leftId, candidate.rightId)}
        aria-label={`Review ${candidate.left} and ${candidate.right}`}
        className="-mx-2 flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left hover:bg-surface-sunken"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-base font-medium text-content-default">
            {candidate.left} <span className="text-content-subtle">&</span> {candidate.right}
          </span>
          <span className="block text-xs text-content-muted">{candidate.reasons.join(' · ')}</span>
        </span>
        <Chip size="md">{Math.round(candidate.score * 100)}%</Chip>
        <Icon icon={faChevronRight} className="size-3 text-content-subtle" />
      </button>
    </li>
  )
}

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  middleName: 'Middle name',
  nickname: 'Nickname',
  prefix: 'Prefix',
  suffix: 'Suffix',
  gender: 'Gender',
  birthdate: 'Birthdate',
  anniversary: 'Anniversary',
  maritalStatus: 'Marital status',
  membershipStatus: 'Status',
  campusId: 'Campus',
  avatarUrl: 'Photo',
}

function MergePicker({ pair, onClose }: { pair: Pair; onClose: () => void }) {
  const [swapped, setSwapped] = useState(false)
  const winnerId = swapped ? pair.loserId : pair.winnerId
  const loserId = swapped ? pair.winnerId : pair.loserId

  const { data: preview, isPending, error } = useMergePreview(winnerId, loserId)
  const merge = useMergePeople()
  const navigate = useNavigate()
  // Only overrides live here; anything untouched follows the server's
  // suggestion, which is what the radios already display.
  const [choices, setChoices] = useState<Record<string, 'winner' | 'loser'>>({})

  const reset = () => {
    setChoices({})
    setSwapped(false)
  }

  if (isPending) {
    return (
      <Card className="p-4 sm:p-6">
        <Spinner label="Comparing records…" />
      </Card>
    )
  }
  if (error || !preview) {
    return (
      <Card className="p-4 sm:p-6">
        <ErrorNote error={error ?? new Error('Could not compare these records.')} />
        <Button variant="secondary" className="mt-3" onClick={onClose}>
          Close
        </Button>
      </Card>
    )
  }

  const differing = preview.fields.filter((field) => field.winner !== field.loser)
  const identical = preview.fields.length - differing.length
  const moving = Object.entries(preview.moving).filter(([, count]) => count > 0)
  const duplicates = Object.entries(preview.duplicates).filter(([, count]) => count > 0)

  return (
    <Card className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-content-default">
          Merging into <span className="text-content-brand">{preview.winner.name}</span>
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            icon={faRightLeft}
            className="text-xs"
            onClick={() => {
              setSwapped((v) => !v)
              setChoices({})
            }}
          >
            Swap which record survives
          </Button>
          <IconButton icon={faXmark} label="Cancel" onClick={onClose} />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-lg text-sm">
          <thead>
            <tr className="border-b border-border-subtle text-left text-xs uppercase tracking-wide text-content-subtle">
              <th className="py-2 pr-4 font-medium">Field</th>
              <th className="py-2 pr-4 font-medium">
                Keeps: {preview.winner.name}
                <Link
                  to="/people/$id"
                  params={{ id: preview.winner.id }}
                  className="ml-2 font-normal normal-case text-content-brand hover:underline"
                >
                  open
                </Link>
              </th>
              <th className="py-2 font-medium">
                Absorbs: {preview.loser.name}
                <Link
                  to="/people/$id"
                  params={{ id: preview.loser.id }}
                  className="ml-2 font-normal normal-case text-content-brand hover:underline"
                >
                  open
                </Link>
              </th>
            </tr>
          </thead>
          <tbody>
            {differing.map((field) => {
              const chosen = choices[field.field] ?? field.suggested
              return (
                <tr key={field.field} className="border-b border-border-subtle">
                  <td className="py-2 pr-4 text-content-muted">
                    {FIELD_LABELS[field.field] ?? field.field}
                  </td>
                  {(['winner', 'loser'] as const).map((side) => (
                    <td key={side} className="py-2 pr-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`merge-${field.field}`}
                          checked={chosen === side}
                          onChange={() =>
                            setChoices((current) => ({ ...current, [field.field]: side }))
                          }
                        />
                        <span
                          className={
                            chosen === side ? 'text-content-default' : 'text-content-subtle'
                          }
                        >
                          {field[side] ?? <span className="italic">empty</span>}
                        </span>
                      </label>
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {differing.length === 0 && (
        <p className="text-sm text-content-muted">
          These records agree on every field — nothing to choose.
        </p>
      )}
      {identical > 0 && differing.length > 0 && (
        <p className="text-xs text-content-subtle">
          {identical} field{identical === 1 ? '' : 's'} already match and are not shown.
        </p>
      )}

      <div className="rounded-lg bg-surface-sunken p-3 text-xs text-content-muted">
        <p>
          <span className="font-medium text-content-default">Moving across: </span>
          {moving.length === 0
            ? 'nothing'
            : moving.map(([key, count]) => `${count} ${key}`).join(', ')}
        </p>
        {duplicates.length > 0 && (
          <p className="mt-1">
            <span className="font-medium text-content-default">Already on the survivor: </span>
            {duplicates.map(([key, count]) => `${count} ${key}`).join(', ')} — retired, not
            duplicated.
          </p>
        )}
        <p className="mt-1">
          {preview.loser.name} is removed from the directory and their links forward here. This can
          be undone.
        </p>
      </div>

      <ErrorNote error={merge.error} />

      <div className="flex gap-2">
        <Button
          icon={faCodeMerge}
          disabled={merge.isPending}
          onClick={() =>
            merge.mutate(
              { winnerId, loserId, fields: choices },
              {
                onSuccess: () => {
                  reset()
                  onClose()
                  void navigate({ to: '/people/$id', params: { id: winnerId } })
                },
              },
            )
          }
        >
          {merge.isPending ? 'Merging…' : `Merge into ${preview.winner.name}`}
        </Button>
        <Button variant="secondary" icon={faBan} onClick={onClose}>
          Not a duplicate
        </Button>
      </div>
    </Card>
  )
}

function HistoryCard() {
  const { data: history, isPending } = useMergeHistory()
  const undo = useUndoMerge()

  return (
    <Card bleed>
      <ListTitle title="Recent merges" />
      <div className="p-4">
        {isPending ? (
          <Spinner />
        ) : !history || history.length === 0 ? (
          <p className="text-sm text-content-muted">No merges yet.</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {history.map((record) => (
              <li key={record.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm text-content-default">
                    <Link
                      to="/people/$id"
                      params={{ id: record.winnerPersonId }}
                      className="hover:underline"
                    >
                      {record.winnerName}
                    </Link>{' '}
                    <span className="text-content-subtle">absorbed</span> {record.loserName}
                  </p>
                  <p className="text-xs text-content-subtle">
                    {new Date(record.createdAt).toLocaleString()}
                    {record.undoneAt ? ' · undone' : ''}
                  </p>
                </div>
                {record.canUndo && (
                  <ConfirmButton
                    label="Undo"
                    icon={faArrowRotateLeft}
                    className="shrink-0 px-2 py-1 text-xs"
                    variant="secondary"
                    disabled={undo.isPending}
                    title={`Restore ${record.loserName}?`}
                    description="Their contact points, tags, notes and household seats move back."
                    confirmLabel="Undo merge"
                    onConfirm={() => undo.mutate(record.id)}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
        <ErrorNote error={undo.error} />
      </div>
    </Card>
  )
}
